import type {
  StateMonthlyData,
  StateYearlyData,
  StateSummary,
  StateRanking,
  YearSummary,
  ProcessedData,
  StateGeoFeature,
} from '../types';
import { parseFormattedNumber, parseColumnHeader } from './formatters';

/**
 * Parse CSV text into state monthly data
 */
export function parseCSV(csvText: string): StateMonthlyData[] {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];

  // Parse header to get column names (months)
  const headerLine = lines[0];
  const headers = parseCSVLine(headerLine);
  const monthColumns = headers.slice(1); // Skip "State" column

  const stateData: StateMonthlyData[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line);
    const stateName = values[0]?.trim();

    // Skip TOTAL row and empty rows
    if (!stateName || stateName.toUpperCase() === 'TOTAL') continue;

    const monthlyValues = new Map<string, number>();

    for (let j = 1; j < values.length && j <= monthColumns.length; j++) {
      const month = monthColumns[j - 1];
      const value = parseFormattedNumber(values[j] || '0');
      monthlyValues.set(month, value);
    }

    stateData.push({
      state: stateName,
      monthlyValues,
    });
  }

  return stateData;
}

/**
 * Parse a CSV line handling quoted values with commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Aggregate monthly data into yearly summaries
 */
export function aggregateByYear(
  monthlyData: StateMonthlyData[]
): Map<string, StateSummary> {
  const summaries = new Map<string, StateSummary>();

  for (const stateData of monthlyData) {
    const yearlyData = new Map<number, StateYearlyData>();
    const yearMonths = new Map<number, { month: string; value: number }[]>();

    // Group by year
    for (const [monthKey, value] of stateData.monthlyValues) {
      const parsed = parseColumnHeader(monthKey);
      if (!parsed) continue;

      if (!yearMonths.has(parsed.year)) {
        yearMonths.set(parsed.year, []);
      }
      yearMonths.get(parsed.year)!.push({ month: monthKey, value });
    }

    // Calculate yearly aggregates
    let allTimeTotal = 0;
    let allTimeCount = 0;
    let earliestYear = Infinity;
    let latestYear = -Infinity;
    let firstYearTotal = 0;
    let lastYearTotal = 0;

    const sortedYears = Array.from(yearMonths.keys()).sort((a, b) => a - b);

    for (const year of sortedYears) {
      const months = yearMonths.get(year)!;
      const values = months.map((m) => m.value);
      const total = values.reduce((a, b) => a + b, 0);
      const average = total / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);

      yearlyData.set(year, {
        state: stateData.state,
        year,
        total,
        average,
        min,
        max,
        monthlyBreakdown: months.sort((a, b) => {
          const parsedA = parseColumnHeader(a.month);
          const parsedB = parseColumnHeader(b.month);
          if (!parsedA || !parsedB) return 0;
          return parsedA.year - parsedB.year || 
            new Date(`${parsedA.month} 1, ${parsedA.year}`).getMonth() - 
            new Date(`${parsedB.month} 1, ${parsedB.year}`).getMonth();
        }),
      });

      allTimeTotal += total;
      allTimeCount += values.length;

      if (year < earliestYear) {
        earliestYear = year;
        firstYearTotal = total;
      }
      if (year > latestYear) {
        latestYear = year;
        lastYearTotal = total;
      }
    }

    const percentChangeFromStart =
      firstYearTotal > 0
        ? ((lastYearTotal - firstYearTotal) / firstYearTotal) * 100
        : 0;

    summaries.set(stateData.state, {
      state: stateData.state,
      yearlyData,
      allTimeTotal,
      allTimeAverage: allTimeCount > 0 ? allTimeTotal / allTimeCount : 0,
      percentChangeFromStart,
      latestYear: latestYear === -Infinity ? new Date().getFullYear() : latestYear,
      earliestYear: earliestYear === Infinity ? 2007 : earliestYear,
    });
  }

  return summaries;
}

/**
 * Create year summaries with rankings
 */
export function createYearSummaries(
  stateSummaries: Map<string, StateSummary>
): Map<number, YearSummary> {
  const yearSummaries = new Map<number, YearSummary>();

  // Get all unique years
  const allYears = new Set<number>();
  for (const summary of stateSummaries.values()) {
    for (const year of summary.yearlyData.keys()) {
      allYears.add(year);
    }
  }

  for (const year of allYears) {
    const stateYearData: { state: string; total: number }[] = [];

    for (const [state, summary] of stateSummaries) {
      const yearData = summary.yearlyData.get(year);
      if (yearData) {
        stateYearData.push({ state, total: yearData.total });
      }
    }

    // Sort by total descending
    stateYearData.sort((a, b) => b.total - a.total);

    const totalAllocation = stateYearData.reduce((sum, d) => sum + d.total, 0);
    const values = stateYearData.map((d) => d.total);
    const sortedValues = [...values].sort((a, b) => a - b);
    const medianIndex = Math.floor(sortedValues.length / 2);
    const medianAllocation =
      sortedValues.length % 2 === 0
        ? (sortedValues[medianIndex - 1] + sortedValues[medianIndex]) / 2
        : sortedValues[medianIndex];

    const rankings: StateRanking[] = stateYearData.map((d, i) => ({
      state: d.state,
      rank: i + 1,
      total: d.total,
      percentOfTotal: totalAllocation > 0 ? (d.total / totalAllocation) * 100 : 0,
    }));

    yearSummaries.set(year, {
      year,
      totalAllocation,
      rankings,
      minState: stateYearData[stateYearData.length - 1] || { state: '', total: 0 },
      maxState: stateYearData[0] || { state: '', total: 0 },
      averageAllocation: stateYearData.length > 0 ? totalAllocation / stateYearData.length : 0,
      medianAllocation,
    });
  }

  return yearSummaries;
}

/**
 * Load and process the complete dataset
 */
export async function loadData(): Promise<ProcessedData> {
  // Load CSV and GeoJSON in parallel
  const [csvResponse, geoResponse] = await Promise.all([
    fetch('/data/FAAC Data - Monthly_state.csv'),
    fetch('/data/nigeria_state_boundaries_xsmall.json'),
  ]);

  const csvText = await csvResponse.text();
  const geoJson = await geoResponse.json();

  // Parse and process data
  const monthlyData = parseCSV(csvText);
  const stateSummaries = aggregateByYear(monthlyData);
  const yearSummaries = createYearSummaries(stateSummaries);

  // Get state names and years
  const states = Array.from(stateSummaries.keys()).sort();
  const years = Array.from(yearSummaries.keys()).sort((a, b) => a - b);

  // Find global min/max
  let minAllocation = Infinity;
  let maxAllocation = -Infinity;

  for (const yearSummary of yearSummaries.values()) {
    for (const ranking of yearSummary.rankings) {
      if (ranking.total < minAllocation) minAllocation = ranking.total;
      if (ranking.total > maxAllocation) maxAllocation = ranking.total;
    }
  }

  // Process GeoJSON features
  const geoFeatures: StateGeoFeature[] = geoJson.features.map(
    (feature: StateGeoFeature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        allocation: 0,
        rank: 0,
        percentChange: 0,
      },
    })
  );

  // Get data range
  const allMonths = Array.from(monthlyData[0]?.monthlyValues.keys() || []);
  const sortedMonths = allMonths.sort((a, b) => {
    const parsedA = parseColumnHeader(a);
    const parsedB = parseColumnHeader(b);
    if (!parsedA || !parsedB) return 0;
    return parsedA.year - parsedB.year || 
      new Date(`${parsedA.month} 1, 2000`).getMonth() - 
      new Date(`${parsedB.month} 1, 2000`).getMonth();
  });

  return {
    states,
    years,
    stateSummaries,
    yearSummaries,
    geoFeatures,
    minAllocation: minAllocation === Infinity ? 0 : minAllocation,
    maxAllocation: maxAllocation === -Infinity ? 0 : maxAllocation,
    dataRange: {
      startYear: years[0] || 2007,
      endYear: years[years.length - 1] || 2025,
      startMonth: sortedMonths[0] || 'Jan-2007',
      endMonth: sortedMonths[sortedMonths.length - 1] || 'Jun-2025',
    },
  };
}

/**
 * Update GeoJSON features with allocation data for a specific year
 */
export function updateGeoFeaturesForYear(
  features: StateGeoFeature[],
  yearSummary: YearSummary,
  stateSummaries: Map<string, StateSummary>
): StateGeoFeature[] {
  const rankingMap = new Map(
    yearSummary.rankings.map((r) => [r.state, r])
  );

  return features.map((feature) => {
    const stateName = feature.properties.admin1Name;
    const ranking = rankingMap.get(stateName);
    const summary = stateSummaries.get(stateName);

    return {
      ...feature,
      properties: {
        ...feature.properties,
        allocation: ranking?.total ?? 0,
        rank: ranking?.rank ?? 0,
        percentChange: summary?.percentChangeFromStart ?? 0,
      },
    };
  });
}

/**
 * Get top N states for a year
 */
export function getTopNStates(
  yearSummary: YearSummary,
  n: number
): StateRanking[] {
  return yearSummary.rankings.slice(0, n);
}

/**
 * Get state data for comparison
 */
export function getComparisonData(
  states: string[],
  stateSummaries: Map<string, StateSummary>,
  years: number[]
): { state: string; data: { year: number; total: number }[] }[] {
  return states.map((state) => {
    const summary = stateSummaries.get(state);
    const data = years.map((year) => ({
      year,
      total: summary?.yearlyData.get(year)?.total ?? 0,
    }));
    return { state, data };
  });
}

